// Clipboard copy/paste support for the grid controller.
//
// AG Grid's built-in clipboard is an Enterprise feature, so we implement
// our own. Supports:
//   - Copy: focused cell value, or all selected rows as TSV
//   - Paste: single value into focused cell, or TSV block across cells
//
// Usage from the grid controller:
//   import * as clipboard from "../grid/clipboard"
//
//   connect()    → clipboard.install(this)
//   disconnect() → clipboard.uninstall(this)

export function install(controller) {
  controller._clipboard_onPaste = onPaste.bind(controller)
  controller.element.addEventListener("paste", controller._clipboard_onPaste)

  controller._clipboard_onCopy = onCopy.bind(controller)
  controller.element.addEventListener("copy", controller._clipboard_onCopy)
}

export function uninstall(controller) {
  controller.element.removeEventListener("paste", controller._clipboard_onPaste)
  controller.element.removeEventListener("copy", controller._clipboard_onCopy)
}

// ── Copy ──────────────────────────────────────────────────

function onCopy(event) {
  if (!this.gridApi) return

  // Don't intercept if we're editing — let the native input copy work
  if (this.gridApi.getEditingCells().length > 0) return

  const focusedCell = this.gridApi.getFocusedCell()
  if (!focusedCell) return

  event.preventDefault()

  const columns = this.gridApi.getAllDisplayedColumns()
  const selectedRows = this.gridApi.getSelectedRows()

  let tsvLines

  if (selectedRows.length > 1) {
    // Multiple rows selected — copy all selected rows across all data columns
    const dataColumns = columns.filter((col) => {
      const field = col.getColDef().field
      return field && !field.startsWith("_")
    })

    tsvLines = selectedRows.map((row) =>
      dataColumns.map((col) => copyValueForColumn(col, row)).join("\t")
    )
  } else {
    // Single focused cell — copy just that value
    const rowNode = this.gridApi.getDisplayedRowAtIndex(focusedCell.rowIndex)
    if (!rowNode || rowNode.rowPinned) return

    const field = focusedCell.column.getColDef().field
    if (!field) return

    tsvLines = [copyValueForColumn(focusedCell.column, rowNode.data)]
  }

  const text = tsvLines.join("\n")
  if (event.clipboardData) {
    event.clipboardData.setData("text/plain", text)
  } else {
    navigator.clipboard.writeText(text)
  }
}

// ── Paste ─────────────────────────────────────────────────

function onPaste(event) {
  if (!this.gridApi) return

  const focusedCell = this.gridApi.getFocusedCell()
  if (!focusedCell) return

  // Don't intercept if we're already editing — let the input handle it
  if (this.gridApi.getEditingCells().length > 0) return

  event.preventDefault()

  const text = (event.clipboardData || window.clipboardData).getData("text")
  if (!text) return

  const rows = text.split(/\r?\n/).filter((r) => r.length > 0)
  const columns = this.gridApi.getColumns()
  const startColIndex = columns.indexOf(focusedCell.column)

  // Count only non-pinned data rows
  let dataRowCount = 0
  this.gridApi.forEachNode((node) => { if (!node.rowPinned) dataRowCount++ })

  if (this.addableValue) {
    // Ensure enough empty rows exist for the paste block
    const neededRows = focusedCell.rowIndex + rows.length - dataRowCount
    for (let i = 0; i < neededRows; i++) {
      this.gridApi.applyTransaction({ add: [{ id: this.nextEmptyId() }] })
    }
  } else {
    // Trim paste to fit existing rows
    rows.length = Math.min(rows.length, dataRowCount - focusedCell.rowIndex)
  }

  let pastedIntoEmpty = false

  rows.forEach((row, rowOffset) => {
    const values = row.split("\t")
    const rowIndex = focusedCell.rowIndex + rowOffset
    const rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex)
    if (!rowNode) return

    if (Object.keys(rowNode.data).every((field) => field === "id")) pastedIntoEmpty = true

    values.forEach((value, colOffset) => {
      const col = columns[startColIndex + colOffset]
      if (!col) return

      // Skip non-editable cells
      const colDef = col.getColDef()
      const editable = typeof colDef.editable === "function"
        ? colDef.editable({ node: rowNode, data: rowNode.data, column: col, colDef })
        : colDef.editable
      if (editable === false) return

      const field = colDef.field
      if (!field) return

      // For select editors, match pasted text against allowed values/labels
      if (colDef.cellEditor === "agSelectCellEditor") {
        const allowed = colDef.cellEditorParams?.values || []
        let matched = allowed.find((v) => v === value)
          || allowed.find((v) => v.toLowerCase() === value.toLowerCase())
        // Try matching against formatted labels (e.g. "Wholesale" → "wholesale")
        if (!matched && colDef.valueFormatter) {
          matched = allowed.find((v) => {
            const label = colDef.valueFormatter({ value: v, data: rowNode.data })
            return label && label.toLowerCase() === value.toLowerCase()
          })
        }
        if (!matched) return
        rowNode.setDataValue(field, matched, "paste")
      } else if (colDef.cellEditorParams?.valueField) {
        // Pasted text uses the same displayed-label contract as editor selection.
        // Clear the backing selection so it cannot disagree with the new label.
        const valueField = colDef.cellEditorParams.valueField
        rowNode.data[valueField] = null
        this.syncRowToDOM(rowNode.data.id, { [valueField]: null })
        rowNode.setDataValue(field, value, "paste")
      } else if (colDef.cellEditorParams?.pasteAsDisplayValue) {
        rowNode.setDataValue(field, value, "paste")
      } else if (colDef.cellEditor && colDef.cellEditor !== "agTextCellEditor") {
        // Skip unknown custom editors
        return
      } else {
        rowNode.setDataValue(field, value, "paste")
      }
    })
  })

  this.gridApi.refreshCells({ force: true })
  this.refreshTotals()

  // If we pasted into an empty row, it'll get saved and receive a database
  // record ID — append a fresh empty row so there's always one ready.
  if (this.addableValue && pastedIntoEmpty) {
    this.gridApi.applyTransaction({ add: [{ id: this.nextEmptyId() }] })
  }
}

// ── Helpers ───────────────────────────────────────────────

function copyValueForColumn(col, rowData) {
  return String(rowData[col.getColDef().field] ?? "")
}
